/**
 * <compass-needle-particles> — canvas overlay that emits 8 unique
 * particle species from BOTH tips of the rotating compass needle.
 * Phenomenal spray physics:
 *
 *   • Velocity inheritance — particles inherit the tip's tangential
 *     velocity (computed per-frame from tip-position delta), so they
 *     get SLUNG outward like sparks off a sword swing.
 *   • Curl-noise wind field — a low-frequency 2D pseudo-curl field
 *     evolves over time and gently pushes every particle, so plumes
 *     undulate instead of falling in straight lines.
 *   • Per-stem signature forces:
 *       lead-vocals → buoyant rise + flicker
 *       backing-vocals → orbital swirl around tip
 *       drums → shockwave blast w/ rapid decay
 *       bass → heavy gravity, lingers and settles
 *       guitar → sine-snake (perpendicular wobble)
 *       percussion → brownian jitter (high-frequency noise)
 *       strings → ribbon flowing in wind, very long drift
 *       synth → magnet pull toward compass center, spirals home
 *
 *   • Additive blending + radial-gradient halos for cinematic glow.
 */

import { readAllStems, mix, clamp } from "./stem-levels.ts";

type StemKey =
  | "lead-vocals"
  | "backing-vocals"
  | "drums"
  | "bass"
  | "guitar"
  | "percussion"
  | "strings"
  | "synth";

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  ax: number;
  ay: number;
  ageMs: number;
  lifeMs: number;
  hue: number;
  satL: [number, number];
  size: number;
  drag: number;
  shape: "dot" | "streak" | "puff" | "spiral" | "ribbon";
  trail: Array<{ x: number; y: number }>;
  rotPhase: number;
  alpha: number;
  stem: StemKey;
  birthAngle: number;        // angle particle was emitted at (for snake-wave)
  // Anchor for swirl/magnet stems (e.g. tip origin for orbital, center for magnet)
  ax0: number;
  ay0: number;
  jitterSeed: number;        // unique per-particle phase
};

const MAX_PARTICLES = 320;

type SpawnConfig = {
  readonly count: number;
  readonly cadenceMs: number;
  readonly threshold: number;
};

const SPAWN: Readonly<Record<StemKey, SpawnConfig>> = {
  "lead-vocals":    { count: 2, cadenceMs:  70, threshold: 0.08 },
  "backing-vocals": { count: 1, cadenceMs: 120, threshold: 0.10 },
  "drums":          { count: 4, cadenceMs:  40, threshold: 0.16 },
  "bass":           { count: 2, cadenceMs: 160, threshold: 0.14 },
  "guitar":         { count: 2, cadenceMs: 100, threshold: 0.12 },
  "percussion":     { count: 5, cadenceMs:  35, threshold: 0.10 },
  "strings":        { count: 1, cadenceMs: 140, threshold: 0.08 },
  "synth":          { count: 1, cadenceMs: 160, threshold: 0.10 },
};

const rand = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);

/** Lightweight pseudo-curl noise — value-noise gradient rotated 90°.
 *  Returns a divergence-free 2D vector field (so plumes don't pile up).
 *  Not "true" simplex curl — cheap, smooth, beautiful enough. */
const hash2 = (ix: number, iy: number): number => {
  const s = Math.sin(ix * 127.1 + iy * 311.7) * 43758.5453;
  return s - Math.floor(s);
};
const smoothstep = (t: number): number => t * t * (3 - 2 * t);
const valueNoise = (x: number, y: number): number => {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const a = hash2(ix, iy);
  const b = hash2(ix + 1, iy);
  const c = hash2(ix, iy + 1);
  const d = hash2(ix + 1, iy + 1);
  const u = smoothstep(fx);
  const v = smoothstep(fy);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
};

class CompassNeedleParticles extends HTMLElement {
  #canvas: HTMLCanvasElement | null = null;
  #ctx: CanvasRenderingContext2D | null = null;
  #rafId: number | null = null;
  #lastFrame: number = 0;
  #lastSpawn: Map<StemKey, number> = new Map();
  #particles: Particle[] = [];
  #w: number = 0;
  #h: number = 0;
  #dpr: number = 1;
  #needle: SVGGElement | null = null;
  #svg: SVGSVGElement | null = null;
  #visibilityHandler: (() => void) | null = null;
  // Track previous tip positions to derive tangential velocity (sling).
  #prevNorth: { x: number; y: number } | null = null;
  #prevSouth: { x: number; y: number } | null = null;
  #tipVNorth: { x: number; y: number } = { x: 0, y: 0 };
  #tipVSouth: { x: number; y: number } = { x: 0, y: 0 };
  // Compass center in canvas coords — used by synth magnet-stem.
  #center: { x: number; y: number } = { x: 0, y: 0 };
  // Wind field time evolution.
  #windT: number = 0;

  connectedCallback(): void {
    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;display:block;";
    this.style.position = "absolute";
    this.style.inset = "0";
    this.style.pointerEvents = "none";
    this.style.zIndex = "5";
    this.appendChild(canvas);
    this.#canvas = canvas;
    this.#ctx = canvas.getContext("2d");
    if (this.#ctx === null) return;

    this.#findNeedle();
    this.#resize();

    const ro = new ResizeObserver(() => this.#resize());
    ro.observe(this);

    this.#visibilityHandler = (): void => {
      if (document.hidden) this.#stop();
      else this.#start();
    };
    document.addEventListener("visibilitychange", this.#visibilityHandler);

    this.#start();
  }

  disconnectedCallback(): void {
    this.#stop();
    if (this.#visibilityHandler !== null) {
      document.removeEventListener("visibilitychange", this.#visibilityHandler);
      this.#visibilityHandler = null;
    }
    this.#particles = [];
  }

  #findNeedle(): void {
    let p: HTMLElement | null = this.parentElement;
    while (p) {
      const svg = p.querySelector<SVGSVGElement>("svg");
      const n = p.querySelector<SVGGElement>(".compass-needle--mana");
      if (svg && n) {
        this.#svg = svg;
        this.#needle = n;
        return;
      }
      p = p.parentElement;
    }
  }

  #resize(): void {
    if (!this.#canvas || !this.#ctx) return;
    const dpr = window.devicePixelRatio || 1;
    this.#dpr = dpr;
    this.#w = this.clientWidth || window.innerWidth;
    this.#h = this.clientHeight || window.innerHeight;
    this.#canvas.width = Math.max(1, Math.floor(this.#w * dpr));
    this.#canvas.height = Math.max(1, Math.floor(this.#h * dpr));
    this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.#center = { x: this.#w / 2, y: this.#h / 2 };
  }

  /** Live tip positions in canvas coords via getScreenCTM (which
   *  reflects the live CSS rotation of the needle group). */
  #tipPositions(): { north: { x: number; y: number } | null; south: { x: number; y: number } | null } {
    if (!this.#needle || !this.#svg) {
      this.#findNeedle();
      if (!this.#needle || !this.#svg) return { north: null, south: null };
    }
    try {
      const needle = this.#needle as SVGGraphicsElement;
      const ctm = needle.getScreenCTM();
      if (!ctm) return { north: null, south: null };
      const svg = this.#svg;
      const canvasRect = this.getBoundingClientRect();
      const pn = svg.createSVGPoint();
      pn.x = 400;
      pn.y = 270;
      const ns = pn.matrixTransform(ctm);
      const ps = svg.createSVGPoint();
      ps.x = 400;
      ps.y = 530;
      const ss = ps.matrixTransform(ctm);
      // Also derive compass center (400,400) in canvas coords.
      const pc = svg.createSVGPoint();
      pc.x = 400;
      pc.y = 400;
      const cs = pc.matrixTransform(ctm);
      this.#center = { x: cs.x - canvasRect.left, y: cs.y - canvasRect.top };
      return {
        north: { x: ns.x - canvasRect.left, y: ns.y - canvasRect.top },
        south: { x: ss.x - canvasRect.left, y: ss.y - canvasRect.top },
      };
    } catch {
      return { north: null, south: null };
    }
  }

  #start(): void {
    if (this.#rafId !== null) return;
    this.#lastFrame = performance.now();
    const tick = (now: number): void => {
      const dt = Math.min(50, now - this.#lastFrame);
      this.#lastFrame = now;
      this.#step(dt, now);
      this.#render(now);
      this.#rafId = requestAnimationFrame(tick);
    };
    this.#rafId = requestAnimationFrame(tick);
  }

  #stop(): void {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /** Sample a divergence-free wind vector at world position (x,y) and time t. */
  #wind(x: number, y: number, t: number): { x: number; y: number } {
    const s = 0.004;     // spatial scale (low freq → big swirls)
    const eps = 1.0;
    const n1 = valueNoise(x * s + t * 0.07, y * s);
    const n2 = valueNoise(x * s + t * 0.07, y * s + eps);
    const n3 = valueNoise(x * s + eps + t * 0.07, y * s);
    // Curl of scalar noise field → divergence-free 2D vector.
    const dx = (n2 - n1) / eps;
    const dy = (n3 - n1) / eps;
    return { x: -dy, y: dx };
  }

  #spawnOne(
    key: StemKey,
    lvl: number,
    x: number,
    y: number,
    fromNorth: boolean,
    tipV: { x: number; y: number },
  ): void {
    if (this.#particles.length >= MAX_PARTICLES) this.#particles.shift();

    // WAKE MODE — particles are born where the tip is and STAY there,
    // forming a trail of the needle's swept path. The sling that used
    // to throw them outward is gone (was 0.75 → 0). Initial speeds
    // are tiny (a few px/sec) so motion comes from the needle moving
    // through space, not from the particle's own ejection energy.
    const baseAngle = fromNorth ? -Math.PI / 2 : Math.PI / 2;
    const spread = mix(0.3, 0.8, lvl);
    const needleAng = this.#needleAngle();
    const angle = baseAngle + (Math.random() - 0.5) * spread + needleAng;
    // Tiny inheritance of tip velocity — just enough that the trail
    // visibly lags BEHIND the moving tip (drift, not throw).
    const slingX = tipV.x * 0.05;
    const slingY = tipV.y * 0.05;
    const speedFactor = mix(0.5, 1.2, lvl);
    const birthAng = angle;

    let p: Particle;
    switch (key) {
      case "lead-vocals": {
        // Cream-gold motes that hang in place and twinkle. Wake-mode:
        // tiny speed, settle right where the tip passed.
        const speed = rand(4, 18) * speedFactor;
        p = {
          x, y,
          vx: Math.cos(angle) * speed + slingX,
          vy: Math.sin(angle) * speed + slingY,
          ax: 0, ay: 0,
          ageMs: 0, lifeMs: rand(1800, 2800),
          // Cream-gold (was hue 48 — now warmer toward 52, sat lifted).
          hue: 52, satL: [88, 86],
          size: rand(1.0, 1.8),                    // bigger for radiance
          drag: 0.988,
          shape: "dot", trail: [], rotPhase: Math.random() * Math.PI * 2,
          alpha: rand(0.75, 1),
          stem: key, birthAngle: birthAng,
          ax0: x, ay0: y, jitterSeed: Math.random() * 1000,
        };
        break;
      }
      case "backing-vocals": {
        // Amethyst-gold jewel orbits — gentle hang, slow drift.
        const speed = rand(3, 12) * speedFactor;
        p = {
          x, y,
          vx: Math.cos(angle) * speed + slingX,
          vy: Math.sin(angle) * speed + slingY,
          ax: 0, ay: 0,
          ageMs: 0, lifeMs: rand(2400, 3600),
          // Amethyst kept as the only cool jewel accent in the palette.
          hue: 275, satL: [55, 72],
          size: rand(1.1, 1.8),
          drag: 0.993,
          shape: "spiral", trail: [], rotPhase: Math.random() * Math.PI * 2,
          alpha: rand(0.55, 0.85),
          stem: key, birthAngle: birthAng,
          ax0: x, ay0: y, jitterSeed: Math.random() * 1000,
        };
        break;
      }
      case "drums": {
        // Quick gold flash-trails — short tapered glints right at the
        // tip, NOT shockwave-launched outward.
        const speed = rand(20, 60) * speedFactor;
        p = {
          x, y,
          vx: Math.cos(angle) * speed + slingX,
          vy: Math.sin(angle) * speed + slingY,
          ax: 0, ay: 0,
          ageMs: 0, lifeMs: rand(450, 800),
          // Pure gold.
          hue: 48, satL: [90, 76],
          size: rand(0.8, 1.4),
          drag: 0.978,
          shape: "streak", trail: [], rotPhase: 0,
          alpha: rand(0.85, 1),
          stem: key, birthAngle: birthAng,
          ax0: x, ay0: y, jitterSeed: Math.random() * 1000,
        };
        break;
      }
      case "bass": {
        // Amber wake orbs — sit on the tip's rotation arc and fade in
        // place so the stream of spawns reads as a glowing trail along
        // the path the tip has taken. No gravity of any kind.
        const speed = rand(5, 25) * speedFactor;
        p = {
          x, y,
          vx: Math.cos(angle) * speed + slingX,
          vy: Math.sin(angle) * speed + slingY,
          ax: 0, ay: 0,
          ageMs: 0, lifeMs: rand(2400, 3800),
          hue: 42, satL: [72, 60],
          size: rand(2.2, 4.2),
          drag: 0.985,
          shape: "puff", trail: [], rotPhase: 0,
          alpha: rand(0.35, 0.55),
          stem: key, birthAngle: birthAng,
          ax0: x, ay0: y, jitterSeed: Math.random() * 1000,
        };
        break;
      }
      case "guitar": {
        // Citrine glints — wake-mode, small dots that hang and twinkle.
        const speed = rand(4, 16) * speedFactor;
        p = {
          x, y,
          vx: Math.cos(angle) * speed + slingX,
          vy: Math.sin(angle) * speed + slingY,
          ax: 0, ay: 0,
          ageMs: 0, lifeMs: rand(1600, 2600),
          // Pulled fully into gold band (was 105 olive — now 60 pure citrine).
          hue: 60, satL: [80, 78],
          size: rand(1.0, 1.6),
          drag: 0.988,
          shape: "dot", trail: [], rotPhase: 0,
          alpha: rand(0.7, 0.95),
          stem: key, birthAngle: birthAng,
          ax0: x, ay0: y, jitterSeed: Math.random() * 1000,
        };
        break;
      }
      case "percussion": {
        // Pale-gold pinpricks — twinkle in place along the wake.
        const speed = rand(6, 22) * speedFactor;
        p = {
          x, y,
          vx: Math.cos(angle) * speed + slingX,
          vy: Math.sin(angle) * speed + slingY,
          ax: 0, ay: 0,
          ageMs: 0, lifeMs: rand(550, 950),
          // Cream-gold (was 88 — pulled further toward warm cream).
          hue: 56, satL: [38, 94],
          size: rand(0.6, 1.0),
          drag: 0.980,
          shape: "dot", trail: [], rotPhase: 0,
          alpha: rand(0.75, 1),
          stem: key, birthAngle: birthAng,
          ax0: x, ay0: y, jitterSeed: Math.random() * 1000,
        };
        break;
      }
      case "strings": {
        // Gold-leaf ribbons that flow with the wake — short trails so
        // they read as movement-residue not free-flying streamers.
        const speed = rand(5, 18) * speedFactor;
        p = {
          x, y,
          vx: Math.cos(angle) * speed + slingX,
          vy: Math.sin(angle) * speed + slingY,
          ax: 0, ay: 0,
          ageMs: 0, lifeMs: rand(2400, 3800),
          hue: 50, satL: [85, 80],
          size: rand(1.1, 1.8),
          drag: 0.993,
          shape: "ribbon", trail: [], rotPhase: 0,
          alpha: rand(0.55, 0.85),
          stem: key, birthAngle: birthAng,
          ax0: x, ay0: y, jitterSeed: Math.random() * 1000,
        };
        break;
      }
      case "synth": {
        // Orbital satellite balls — launched TANGENTIALLY around the
        // compass center (perpendicular to the radius from center to
        // spawn point) so the magnet pull in the signature switch
        // gives them true elliptical orbits. Speed tuned to magnet
        // strength: v ≈ sqrt(pull * radius) for circular orbit.
        const cx = this.#center.x;
        const cy = this.#center.y;
        const rx = x - cx;
        const ry = y - cy;
        const radius = Math.hypot(rx, ry) || 1;
        // Tangent (counter-clockwise around center). Flip sign every
        // other spawn so we get both CW and CCW orbits — visually richer.
        const dir = Math.random() < 0.5 ? 1 : -1;
        const tangentX = (-ry / radius) * dir;
        const tangentY = (rx / radius) * dir;
        const speed = rand(120, 180) * speedFactor;
        p = {
          x, y,
          vx: tangentX * speed,
          vy: tangentY * speed,
          ax: 0, ay: 0,
          ageMs: 0, lifeMs: rand(4500, 7000),
          // Amber-rose — small warm jewel orbs.
          hue: 36, satL: [70, 70],
          size: rand(2.4, 4.0),
          drag: 0.9995,                             // near-zero drag — orbit holds shape
          shape: "puff", trail: [], rotPhase: Math.random() * Math.PI * 2,
          alpha: rand(0.45, 0.70),
          stem: key, birthAngle: birthAng,
          ax0: cx, ay0: cy, jitterSeed: Math.random() * 1000,
        };
        break;
      }
    }
    this.#particles.push(p);
  }

  /** Needle CSS rotation angle (radians). Used for spawn direction. */
  #needleAngle(): number {
    if (!this.#needle) {
      this.#findNeedle();
      if (!this.#needle) return 0;
    }
    const transform = getComputedStyle(this.#needle).transform;
    if (transform === "none") return 0;
    const m = transform.match(/matrix\(([^)]+)\)/);
    if (!m || !m[1]) return 0;
    const parts = m[1].split(",").map((s) => parseFloat(s));
    const a = parts[0] ?? 1;
    const b = parts[1] ?? 0;
    return Math.atan2(b, a);
  }

  #step(dt: number, now: number): void {
    const stems = readAllStems();
    const tips = this.#tipPositions();
    const dts = dt / 1000;
    this.#windT += dts;

    // Tangential velocity inheritance — sparks-off-the-sword. EMA-smoothed
    // so noisy frame deltas don't fire wild slings.
    if (tips.north && this.#prevNorth) {
      const vx = (tips.north.x - this.#prevNorth.x) / Math.max(0.0001, dts);
      const vy = (tips.north.y - this.#prevNorth.y) / Math.max(0.0001, dts);
      this.#tipVNorth.x = this.#tipVNorth.x * 0.7 + vx * 0.3;
      this.#tipVNorth.y = this.#tipVNorth.y * 0.7 + vy * 0.3;
    }
    if (tips.south && this.#prevSouth) {
      const vx = (tips.south.x - this.#prevSouth.x) / Math.max(0.0001, dts);
      const vy = (tips.south.y - this.#prevSouth.y) / Math.max(0.0001, dts);
      this.#tipVSouth.x = this.#tipVSouth.x * 0.7 + vx * 0.3;
      this.#tipVSouth.y = this.#tipVSouth.y * 0.7 + vy * 0.3;
    }
    this.#prevNorth = tips.north ?? this.#prevNorth;
    this.#prevSouth = tips.south ?? this.#prevSouth;

    // SPAWN
    for (const key of Object.keys(SPAWN) as StemKey[]) {
      const lvl = stems[key];
      const cfg = SPAWN[key];
      if (lvl < cfg.threshold) continue;
      const last = this.#lastSpawn.get(key) ?? 0;
      const cooldown = mix(cfg.cadenceMs * 2.5, cfg.cadenceMs * 0.4, lvl);
      if (now - last < cooldown) continue;
      this.#lastSpawn.set(key, now);
      for (let i = 0; i < cfg.count; i += 1) {
        if (tips.north) this.#spawnOne(key, lvl, tips.north.x, tips.north.y, true, this.#tipVNorth);
        if (tips.south) this.#spawnOne(key, lvl, tips.south.x, tips.south.y, false, this.#tipVSouth);
      }
    }

    // UPDATE — physics
    // Wind strength scales mildly with overall loudness for "windy when loud".
    const overallStr = getComputedStyle(document.documentElement)
      .getPropertyValue("--st-overall-lvl");
    const overall = clamp(parseFloat(overallStr) || 0, 0, 1);
    const windScale = 60 + overall * 110;

    for (let i = this.#particles.length - 1; i >= 0; i -= 1) {
      const p = this.#particles[i];
      if (!p) continue;
      p.ageMs += dt;
      if (p.ageMs > p.lifeMs) {
        this.#particles.splice(i, 1);
        continue;
      }

      // WAKE-MODE forces — heavily reduced. Every species drifts in
      // place with very mild signature flavour. No more shockwave-bursts,
      // no more magnet pulls, no Brownian shake. The needle moves; the
      // wake is what's left behind it.
      switch (p.stem) {
        case "lead-vocals": {
          // Subtle twinkle — tiny phase shift only.
          p.rotPhase += dts * 5;
          p.vx += Math.cos(p.rotPhase + p.jitterSeed) * 6 * dts;
          break;
        }
        case "backing-vocals": {
          // Very soft swirl around spawn origin.
          const rx = p.x - p.ax0;
          const ry = p.y - p.ay0;
          const len = Math.hypot(rx, ry) || 1;
          p.vx += (-ry / len) * 20 * dts;
          p.vy += (rx / len) * 20 * dts;
          break;
        }
        case "synth": {
          // PURE radial pull toward compass center. Launch was already
          // tangential, so this central acceleration produces stable
          // elliptical orbits. Strength tuned so v²/r ≈ a for the spawn
          // radius — orbits hold their shape for the particle lifetime.
          const rx = this.#center.x - p.x;
          const ry = this.#center.y - p.y;
          const len = Math.hypot(rx, ry) || 1;
          p.vx += (rx / len) * 220 * dts;
          p.vy += (ry / len) * 220 * dts;
          break;
        }
        case "drums":
        case "bass":
        case "guitar":
        case "percussion":
        case "strings": {
          // Pure wake — drift in place. No signature force.
          break;
        }
      }

      // Gentle universal wind so the wake undulates softly instead of
      // sitting frozen. Capped low so motion comes from the spawn pattern.
      // Synth orbits skip wind — they need clean Keplerian paths.
      if (p.stem !== "synth") {
        const w = this.#wind(p.x, p.y, this.#windT);
        p.vx += w.x * windScale * 0.35 * dts;
        p.vy += w.y * windScale * 0.35 * dts;
      }

      // Apply constant accel
      p.vx += p.ax * dts;
      p.vy += p.ay * dts;
      // Drag
      p.vx *= p.drag;
      p.vy *= p.drag;
      // Position
      p.x += p.vx * dts;
      p.y += p.vy * dts;

      // Trail for ribbons/spirals
      if (p.shape === "ribbon" || p.shape === "spiral") {
        p.trail.push({ x: p.x, y: p.y });
        if (p.trail.length > 18) p.trail.shift();
      }
    }
  }

  #render(_now: number): void {
    if (!this.#ctx) return;
    const ctx = this.#ctx;
    ctx.clearRect(0, 0, this.#w, this.#h);
    ctx.globalCompositeOperation = "lighter";

    for (const p of this.#particles) {
      const t = p.ageMs / p.lifeMs;
      let env = 1;
      if (t < 0.06) env = t / 0.06;
      else env = 1 - (t - 0.06) / 0.94;
      env = clamp(env, 0, 1);
      const a = p.alpha * env;
      if (a < 0.02) continue;

      const [sat, light] = p.satL;
      const col = `hsla(${p.hue}, ${sat}%, ${light}%, ${a})`;
      const haloCol = `hsla(${p.hue}, ${sat}%, ${Math.min(95, light + 12)}%, ${a * 0.8})`;

      if (p.shape === "ribbon" || p.shape === "spiral") {
        if (p.trail.length < 2) continue;
        ctx.strokeStyle = col;
        ctx.lineWidth = p.size;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.shadowColor = haloCol;
        ctx.shadowBlur = p.size * 4;
        ctx.beginPath();
        for (let i = 0; i < p.trail.length; i += 1) {
          const pt = p.trail[i];
          if (!pt) continue;
          if (i === 0) ctx.moveTo(pt.x, pt.y);
          else ctx.lineTo(pt.x, pt.y);
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
        continue;
      }

      if (p.shape === "streak") {
        const speedMag = Math.hypot(p.vx, p.vy);
        const len = Math.min(14, speedMag * 0.07);   // short flash-tails only
        const angle = Math.atan2(p.vy, p.vx);
        const tx = p.x - Math.cos(angle) * len;
        const ty = p.y - Math.sin(angle) * len;
        // Iter 3 — three-stop gradient: transparent → deep-gold core → bright tip.
        // The mid-stop gives the trail body weight (was just transparent→bright).
        const grad = ctx.createLinearGradient(tx, ty, p.x, p.y);
        grad.addColorStop(0, `hsla(${p.hue}, ${sat}%, ${light}%, 0)`);
        grad.addColorStop(0.55, `hsla(${p.hue}, ${Math.min(100, sat + 5)}%, ${Math.max(35, light - 18)}%, ${a * 0.75})`);
        grad.addColorStop(1, col);
        ctx.strokeStyle = grad;
        ctx.lineWidth = p.size;
        ctx.lineCap = "round";
        ctx.shadowColor = haloCol;
        ctx.shadowBlur = p.size * 7;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        ctx.shadowBlur = 0;
        continue;
      }

      // Radiant dot/puff — soft halo + colour body. Smaller halo radius
      // than before so additive stacking can't bloom into hot streaks.
      const haloR = p.size * (p.shape === "puff" ? 4 : 3);
      const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, haloR);
      grad.addColorStop(0, col);
      grad.addColorStop(0.45, haloCol.replace(/[\d.]+\)$/, `${a * 0.40})`));
      grad.addColorStop(1, `hsla(${p.hue}, ${sat}%, ${light}%, 0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, haloR, 0, Math.PI * 2);
      ctx.fill();

      // Solid colour body — saturated hue, not white.
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalCompositeOperation = "source-over";
  }
}

export const registerCompassNeedleParticles = (): void => {
  if (customElements.get("compass-needle-particles") !== undefined) return;
  customElements.define("compass-needle-particles", CompassNeedleParticles);
};
